import { AddressFieldMiddleware, useAddressFieldCompletion } from '@adornis/address/client/AddressFieldMiddleware.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { CountryPickerInput } from '@adornis/internationalization/client/language-picker/CountryPickerInput.js';
import { Language } from '@adornis/translation-core/languages.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { html } from 'lit';
import { Gender, NamensTitel } from '../../../db/enums/misc-enums.js';
import { Namensvorsatz } from '../../../db/enums/namensvorsatz.js';
import { Namenszusatz } from '../../../db/enums/namenszusatz.js';
import { toPlainDate } from '../../../db/utils/jobTicketDates.js';
import type { JobTicketFormData } from '../../../db/utils/jobTicketValidator.js';

/**
 * Personal data the transport company needs for the ticket. Read from and written back to the
 * person's Stammdaten and Adresse by the save mutation.
 */
export const JobTicketPersonalFields = stateful((props: { form: FunctionalForm<JobTicketFormData> }) => {
  const addressCompletion = useAddressFieldCompletion({ language: 'de' });

  const geburtsDatumField = props.form.field('geburtsDatum');

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Titel',
        render: DropdownSelectionInput({
          ...props.form.field('titel'),
          selectables: Object.values(NamensTitel),
          placeholder: 'Kein Titel hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Vorname',
        render: TextInput({
          ...props.form.field('vorname'),
          placeholder: 'Kein Vorname hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Nachname',
        render: TextInput({
          ...props.form.field('nachname'),
          placeholder: 'Kein Nachname hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Namenszusatz',
        render: DropdownSelectionInput({
          ...props.form.field('namenszusatz'),
          selectables: Object.values(Namenszusatz),
          placeholder: 'Kein Namenszusatz hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Namensvorsatz',
        render: DropdownSelectionInput({
          ...props.form.field('vorsatzWort'),
          selectables: Object.values(Namensvorsatz),
          placeholder: 'Kein Namensvorsatz hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Geburtsdatum',
        render: DateInput({
          ...geburtsDatumField,
          // the input takes plain dates, the form document hands out proxied ones
          value: toPlainDate(geburtsDatumField.value),
          type: 'date',
          // a birthday cannot be in the future - the legacy picker blocked those days as well
          max: formatCalendarDay(todayAsCalendarDay(), 'yyyy-MM-dd'),
          placeholder: 'Kein Geburtsdatum hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Geschlecht',
        render: DropdownSelectionInput({
          ...props.form.field('geschlecht'),
          selectables: Object.values(Gender),
          placeholder: 'Kein Geschlecht hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Straße und Hausnummer',
        render: DropdownSelectionInput(
          AddressFieldMiddleware({
            ...props.form.field('streetAndNumber'),
            fieldType: 'address',
            addressCompletion,
            placeholder: 'Keine Straße hinterlegt',
            emptyState: 'Keine Adresse gefunden',
            placeholderMode: 'hide',
            clearable: true,
            freeText: true,
          }),
        ),
      })}
      ${FormGridEntry({
        label: 'PLZ / Stadt',
        render: html`
          <div class="flex flex-row gap-2">
            ${DropdownSelectionInput(
              AddressFieldMiddleware({
                ...props.form.field('zip'),
                fieldType: 'zip',
                addressCompletion,
                placeholder: 'PLZ',
                emptyState: 'Keine PLZ gefunden',
                placeholderMode: 'hide',
                clearable: true,
                freeText: true,
              }),
            )}
            ${DropdownSelectionInput(
              AddressFieldMiddleware({
                ...props.form.field('city'),
                fieldType: 'city',
                addressCompletion,
                placeholder: 'Stadt',
                emptyState: 'Kein Ort gefunden',
                placeholderMode: 'hide',
                clearable: true,
                freeText: true,
              }),
            )}
          </div>
        `,
      })}
      ${FormGridEntry({
        label: 'Land',
        render: CountryPickerInput({
          ...props.form.field('country'),
          language: Language.GERMAN,
          showFlags: true,
          placeholder: 'Kein Land hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
    </div>
  `;
});
